import { createContext, type FC, type ReactNode } from "react";
import { BrandInformation, Widget } from "../core/types";
import type { PageStorageScope } from "../core/services/storageService";

export interface WidgetContextType {
  apiUrl: string;
  widgetId: string;
  /**
   * Scope for placement-local persisted UI state (current page, login-return
   * page). Storage keys derive as `<widgetId>:<encodeURIComponent(placementId)>`
   * (or `<widgetId>` alone) — see PageStorageScope. Distinct from the
   * placement-registry key (`placementId || widgetId`) used for postMessage
   * targeting and getCore().
   */
  storageScope: PageStorageScope;
  startMessage: string;
  userEmail: string;
  hubIdentity?: string;
  brandInformation: BrandInformation;
  conversationStarters: [string, string][];
  averageResponseTime: number;
  voiceModeAvailable: boolean;
  /** False when the account has visitor attachments switched off (LAS-4711). */
  attachmentsEnabled: boolean;
  hideToggleButton: boolean;
  homeUrl?: string;
  skipIngest?: boolean;
  theme: Widget["theme"];
  /**
   * True when the embedder declared `<template id="lastbot-custom-main-page">` on
   * the host page. When set, `ChatWidget` routes the `landing | customerInquiry |
   * conversation` page types to the new `CustomMainPage` component instead of
   * cycling through three separate pages. Other system pages (login, enter-code,
   * chat-history, error) still render normally when triggered.
   */
  hasCustomMainPage: boolean;
}

export const WidgetContext = createContext<WidgetContextType | null>(null);

interface WidgetProviderProps {
  children: ReactNode;
  widgetContext: WidgetContextType | null;
}

export const WidgetProvider: FC<WidgetProviderProps> = ({ children, widgetContext }) => {
  return <WidgetContext.Provider value={widgetContext}>{children}</WidgetContext.Provider>;
};
